import { modal, ModalParams } from '@codeleap/modals'
import { View, Text, Image, Button, ActionIcon } from '@/components'
import { AppImages, variantProvider } from '@/app'

const OnboardingModalContent = (props: ModalParams<{}, boolean>) => {
  const { request } = props

  const handleGotIt = () => {
    request.resolve(true)
  }

  const handleClose = () => {
    request.resolve(false)
  }

  return (
    <View variants={['column', 'fullWidth']}>
      <View style={styles.imageContainer}>
        <Image source={AppImages.onboardingContribute} style={styles.image} />

        <ActionIcon
          debugName="onboarding-close"
          name="x"
          variants={['minimal', 'primary3', 'iconSize:3']}
          style={styles.closeIcon}
          onPress={handleClose}
        />
      </View>

      <View variants={['column', 'alignStart', 'gap:3']} style={styles.content}>
        <Text variants={['h2', 'bold', 'color:neutral10']} text="Not sure what to post? We've got you." />

        <View variants={['column', 'gap:3', 'alignStart']}>
          <Text variants={['p1', 'color:neutral8']} text="Just know that:" />

          <View variants={['column', 'gap:2', 'alignStart']}>
            <View variants={['column', 'gap:0.5', 'alignStart']}>
              <Text variants={['p1', 'bold', 'color:neutral10']} text="Hurd is for everyone - not just the experts." />
              <Text
                variants={['p2', 'color:neutral7']}
                text="This is a place to learn, grow, and speak up about what matters to you. Regardless of how 'clued-up' you are."
              />
            </View>

            <View variants={['column', 'gap:0.5', 'alignStart']}>
              <Text variants={['p1', 'bold', 'color:neutral10']} text="You're anonymous by default." />
              <Text
                variants={['p2', 'color:neutral7']}
                text="Your name and face won't appear unless you choose to share them. Feel free to post on behalf of your dog, cat, gecko - whatever. We don't mind."
              />
            </View>

            <View variants={['column', 'gap:0.5', 'alignStart']}>
              <Text variants={['p1', 'bold', 'color:neutral10']} text="We keep you safe." />
              <Text
                variants={['p2', 'color:neutral7']}
                text="This is a moderated, judgement-free space. We're here to support each other - no pile-ons and certainly no trolls!"
              />
            </View>
          </View>
        </View>

        <Button text="Got it!" onPress={handleGotIt} variants={['fullWidth', 'large']} debugName="onboarding-got-it" />
      </View>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    box: {
      width: '100vw',
      maxWidth: 800,
      padding: theme.spacing.value(0),
      overflow: 'hidden',

      [theme.media.down('small')]: {
        maxWidth: '100vw',
        minHeight: '100dvh',
        borderRadius: 0
      }
    },
    imageContainer: {
      width: '100%',
      position: 'relative',
      overflow: 'hidden',
      borderTopLeftRadius: theme.borderRadius.medium,
      borderTopRightRadius: theme.borderRadius.medium,

      [theme.media.down('small')]: {
        borderRadius: 0
      }
    },
    image: {
      width: '100%',
      height: 300,
      objectFit: 'cover',
      display: 'block',

      [theme.media.down('small')]: {
        height: 180
      }
    },
    closeIcon: {
      position: 'absolute',
      top: theme.spacing.value(2),
      right: theme.spacing.value(2),
      zIndex: 10,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      borderRadius: theme.borderRadius.rounded,

      [theme.media.down('small')]: {
        top: theme.spacing.value(1.5),
        right: theme.spacing.value(1.5)
      }
    },
    content: {
      padding: theme.spacing.value(4),

      [theme.media.down('small')]: {
        padding: theme.spacing.value(3)
      }
    }
  }),
  true
)

export const OnboardingModal = modal<{}, boolean>({
  id: 'contribute-onboarding-modal'
})
  .content(OnboardingModalContent)
  .props({
    variants: ['centered'],
    showClose: false,
    styles: {
      box: styles.box,
      innerWrapper: {
        overflowY: 'hidden',
        maxHeight: '100dvh'
      }
    }
  })
